<template>
  <v-container fluid class="pa-0 monitor-page">
    <!-- Header Overlay -->
    <div class="header-overlay pa-3 pr-15 d-flex align-center">
      <v-btn icon @click="$router.back()" class="mr-2 bg-white elev-1">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="white pa-2 rounded-lg elev-1 d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-map-marker-radius</v-icon>
        <span class="font-weight-bold text-subtitle-1">ระบบติดตามร้านค้าและไรเดอร์</span>
      </div>
      <v-spacer />

      <!-- Legend/Stats -->
      <div class="d-flex align-center bg-white pa-2 rounded-lg elev-1 mr-2 hidden-sm-and-down">
        <div class="d-flex align-center mr-4">
          <v-avatar color="orange" size="12" class="mr-2"></v-avatar>
          <span class="text-caption">ร้านค้า ({{ sellers.length }})</span>
        </div>
        <div class="d-flex align-center mr-4">
          <v-avatar color="blue" size="12" class="mr-2"></v-avatar>
          <span class="text-caption">ไรเดอร์ ({{ riders.length }})</span>
        </div>
        <div class="d-flex align-center">
          <v-avatar color="green" size="12" class="mr-2"></v-avatar>
          <span class="text-caption">ออเดอร์ ({{ activeOrders.length }})</span>
        </div>
      </div>

      <v-btn @click="fetchLocations" :loading="isLoading" color="primary" class="rounded-lg elev-1 mr-2">
        <v-icon left>mdi-refresh</v-icon>
        รีเฟรช
      </v-btn>

      <!-- Search List Menu -->
      <v-menu offset-y left max-height="500" :close-on-content-click="false" nudge-bottom="10">
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" color="white" class="rounded-lg elev-1 text-none">
            <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
            รายชื่อพิกัด
          </v-btn>
        </template>
        <v-card width="300" class="rounded-lg">
          <v-toolbar flat dense color="primary" dark>
            <v-toolbar-title class="body-1">ค้นหาตำแหน่ง</v-toolbar-title>
          </v-toolbar>
          <div class="pa-2 pb-0">
             <v-text-field
              v-model="searchQuery"
              prepend-inner-icon="mdi-magnify"
              placeholder="พิมชื่อร้านหรือไรเดอร์..."
              outlined
              dense
              hide-details
              clearable
            ></v-text-field>
          </div>
          <v-list dense class="overflow-y-auto" style="max-height: 350px">
            <!-- Sellers -->
            <v-subheader v-if="filteredSellers.length">ร้านค้า (Sellers)</v-subheader>
            <v-list-item v-for="s in filteredSellers" :key="'list-s-'+s.id" @click="focusOnMarker(s, 'seller')">
              <v-list-item-avatar size="30">
                <v-img :src="s.image || 'https://via.placeholder.com/150'"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ s.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="orange">mdi-store</v-icon>
              </v-list-item-action>
            </v-list-item>

            <!-- Riders -->
            <v-subheader v-if="filteredRiders.length">ไรเดอร์ (Riders)</v-subheader>
            <v-list-item v-for="r in filteredRiders" :key="'list-r-'+r.id" @click="focusOnMarker(r, 'rider')">
              <v-list-item-avatar size="30">
                <v-img :src="r.image || 'https://via.placeholder.com/150'"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ r.name }}</v-list-item-title>
                <v-list-item-subtitle class="caption" :class="r.isOnline ? 'green--text' : 'grey--text'">
                  {{ r.isOnline ? 'Online' : 'Offline' }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small :color="r.isOnline ? 'blue' : 'grey'">mdi-bike</v-icon>
              </v-list-item-action>
            </v-list-item>

            <!-- Active Orders -->
            <v-subheader v-if="filteredOrders.length">ออเดอร์พาหนะ (Active Orders)</v-subheader>
            <v-list-item v-for="o in filteredOrders" :key="'list-o-'+o.id" @click="focusOnMarker({ id: o.id, lat: o.user.lat, lng: o.user.lng }, 'order')">
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold text-truncate" style="max-width: 180px">{{ o.orderNumber }}</v-list-item-title>
                <v-list-item-subtitle>{{ o.status }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon small color="green">mdi-package-variant</v-icon>
              </v-list-item-action>
            </v-list-item>

            <v-list-item v-if="!filteredSellers.length && !filteredRiders.length">
              <v-list-item-content>
                <v-list-item-title class="grey--text text-center italic">ไม่พบข้อมูล</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </div>

    <!-- Map Container -->
    <div ref="mapContainer" class="map-full-screen"></div>

    <!-- Loading Overlay -->
    <v-overlay :value="isLoading && firstLoad" z-index="10">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
/* global google */
import { getAdminMonitorLocationsApi } from "@/services/api/adminServices";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

export default {
  name: "AdminMonitoringMap",
  data() {
    return {
      map: null,
      isLoading: false,
      firstLoad: true,
      sellers: [],
      riders: [],
      activeOrders: [],
      markers: [],
      polylines: [],
      searchRadius: 2000,
      activeCircle: null,
      activeSellerId: null,
      searchQuery: "", // For name filtering
      markerRefs: [], // Store markers with metadata
      refreshInterval: null,
    };
  },
  computed: {
    filteredSellers() {
      if (!this.searchQuery) return this.sellers;
      const q = this.searchQuery.toLowerCase();
      return this.sellers.filter(s => s.name.toLowerCase().includes(q));
    },
    filteredRiders() {
      if (!this.searchQuery) return this.riders;
      const q = this.searchQuery.toLowerCase();
      return this.riders.filter(r => r.name.toLowerCase().includes(q));
    },
    filteredOrders() {
      if (!this.searchQuery) return this.activeOrders;
      const q = this.searchQuery.toLowerCase();
      return this.activeOrders.filter(o => o.orderNumber.toLowerCase().includes(q) || o.status.toLowerCase().includes(q));
    }
  },
  mounted() {
    this.initMap();
    // Auto refresh every 30 seconds
    this.refreshInterval = setInterval(this.fetchLocations, 30000);
  },
  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    this.clearMarkers();
    if (this.activeCircle) {
      this.activeCircle.setMap(null);
      this.activeCircle = null;
    }
  },
  methods: {
    async initMap() {
      try {
        await loadGoogleMapsScript();
        this.setupMap();
      } catch (e) {
        console.warn(e);
      }
    },
    setupMap() {
      const mapStyle = [
        { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
        { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { featureType: "administrative", elementType: "labels", stylers: [{ visibility: "off" }] }
      ];

      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: { lat: 13.7563, lng: 100.5018 }, // Bangkok default
        zoom: 12,
        styles: mapStyle, // Hide POIs
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });

      this.fetchLocations();
    },
    async fetchLocations() {
      this.isLoading = true;
      try {
        const res = await getAdminMonitorLocationsApi();
        if (res.success) {
          this.sellers = res.sellers || [];
          this.riders = res.riders || [];
          this.activeOrders = res.activeOrders || [];
          this.searchRadius = res.searchRadius || 5000;
          this.updateMarkers();
        }
      } catch (error) {
        console.error("Monitor Map Fetch Error:", error);
      } finally {
        this.isLoading = false;
        this.firstLoad = false;
      }
    },
    clearMarkers() {
      this.markers.forEach(m => m.setMap(null));
      this.markers = [];
      this.markerRefs = [];
      this.polylines.forEach(p => p.setMap(null));
      this.polylines = [];
    },
    updateMarkers() {
      this.clearMarkers();
      const bounds = new google.maps.LatLngBounds();
      let hasPoints = false;

      // Add Sellers
      this.sellers.forEach(s => {
        const pos = { lat: parseFloat(s.lat), lng: parseFloat(s.lng) };
        const marker = new google.maps.Marker({
          position: pos,
          map: this.map,
          title: s.name,
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
            scaledSize: new google.maps.Size(40, 40)
          },
          label: {
            text: s.name,
            color: "#E65100",
            fontSize: "12px",
            fontWeight: "bold",
            className: "marker-label"
          }
        });
        
        // Add metadata to marker for searching
        marker.customId = s.id;
        marker.customType = 'seller';
        this.markerRefs.push(marker);

        const infoWindow = new google.maps.InfoWindow({
          content: `<div class="pa-2"><strong>ร้าน: ${s.name}</strong><br>ID: ${s.id}</div>`
        });

        marker.addListener("click", () => {
          // If clicking the same seller, close it (Toggle)
          if (this.activeSellerId === s.id) {
            infoWindow.close();
            if (this.activeCircle) this.activeCircle.setMap(null);
            this.activeCircle = null;
            this.activeSellerId = null;
            return;
          }

          // 1. Show InfoWindow
          infoWindow.open(this.map, marker);
          
          // 2. Draw/Update Radius Circle
          if (this.activeCircle) {
            this.activeCircle.setMap(null);
          }
          
          this.activeCircle = new google.maps.Circle({
            strokeColor: "#FF9800",
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: "#FF9800",
            fillOpacity: 0.15,
            map: this.map,
            center: pos,
            radius: this.searchRadius,
            clickable: false
          });

          this.activeSellerId = s.id;
        });

        // If InfoWindow is closed manually, also remove the circle
        infoWindow.addListener("closeclick", () => {
          if (this.activeCircle) {
            this.activeCircle.setMap(null);
            this.activeCircle = null;
          }
          this.activeSellerId = null;
        });

        this.markers.push(marker);
        bounds.extend(pos);
        hasPoints = true;
      });

      // Add Riders
      this.riders.forEach(r => {
        const pos = { lat: parseFloat(r.lat), lng: parseFloat(r.lng) };
        const marker = new google.maps.Marker({
          position: pos,
          map: this.map,
          title: r.name,
          icon: {
            url: r.isOnline ? "https://maps.google.com/mapfiles/ms/icons/blue-dot.png" : "https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png",
            scaledSize: new google.maps.Size(40, 40)
          },
          label: {
            text: r.name,
            color: "#1565C0",
            fontSize: "11px",
            fontWeight: "bold",
            className: "marker-label-rider"
          }
        });
        
        // Add metadata to marker for searching
        marker.customId = r.id;
        marker.customType = 'rider';
        this.markerRefs.push(marker);

        const infoWindow = new google.maps.InfoWindow({
          content: `<div class="pa-2"><strong>Rider: ${r.name}</strong><br>Status: ${r.isOnline ? 'Online' : 'Offline'}</div>`
        });
        marker.addListener("click", () => infoWindow.open(this.map, marker));

        this.markers.push(marker);
        bounds.extend(pos);
        hasPoints = true;
      });

      // Add Active Orders (User locations and Lines)
      this.activeOrders.forEach(o => {
        const uPos = { lat: parseFloat(o.user.lat), lng: parseFloat(o.user.lng) };
        const sPos = { lat: parseFloat(o.seller.lat), lng: parseFloat(o.seller.lng) };

        // 1. User Marker
        const marker = new google.maps.Marker({
          position: uPos,
          map: this.map,
          title: `Order: ${o.orderNumber}`,
          icon: {
            url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
            scaledSize: new google.maps.Size(40, 40)
          },
          label: {
            text: o.orderNumber,
            color: "#2E7D32",
            fontSize: "11px",
            fontWeight: "bold",
            className: "marker-label-order"
          }
        });

        marker.customId = o.id;
        marker.customType = 'order';
        this.markerRefs.push(marker);

        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div class="pa-2">
              <strong>Order: ${o.orderNumber}</strong><br>
              Status: ${o.status}<br>
              <hr class="my-1">
              Seller ID: ${o.seller.id}<br>
              Rider ID: ${o.rider ? o.rider.id : 'N/A'}<br>
              User ID: ${o.user.id}
            </div>
          `
        });
        marker.addListener("click", () => infoWindow.open(this.map, marker));

        this.markers.push(marker);
        bounds.extend(uPos);
        hasPoints = true;

        // 2. Draw Polyline (Seller -> Rider -> User) if Rider exists
        if (o.rider) {
          const rPos = { lat: parseFloat(o.rider.lat), lng: parseFloat(o.rider.lng) };
          
          const path = [sPos, rPos, uPos];
          const line = new google.maps.Polyline({
            path: path,
            geodesic: true,
            strokeColor: "#2E7D32",
            strokeOpacity: 0.6,
            strokeWeight: 3,
            icons: [{
              icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW },
              offset: '100%',
              repeat: '50px'
            }],
            map: this.map
          });

          // Style dashed if it's still being delivered
          line.setOptions({
            strokeOpacity: 0,
            icons: [{
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                scale: 4
              },
              offset: '0',
              repeat: '20px'
            }, {
              icon: { path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2 },
              offset: '100%'
            }],
          });

          this.polylines.push(line);
        }
      });

      if (hasPoints && this.firstLoad) {
        this.map.fitBounds(bounds);
      }
    },
    focusOnMarker(item, type) {
      if (!this.map || !item.lat || !item.lng) return;
      
      const pos = { lat: parseFloat(item.lat), lng: parseFloat(item.lng) };
      
      // 1. Move map
      this.map.panTo(pos);
      this.map.setZoom(16); // Close zoom for focus
      
      // 2. Find and Trigger Click Event
      const marker = this.markerRefs.find(m => m.customId === item.id && m.customType === type);
      if (marker) {
        google.maps.event.trigger(marker, 'click');
      }
    }
  }
}
</script>

<style scoped>
.monitor-page {
  height: calc(100vh - 64px);
  /* Adjust based on your navbar height */
  position: relative;
  overflow: hidden;
}

.map-full-screen {
  width: 100%;
  height: 100%;
}

.header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  pointer-events: none;
}

.header-overlay>* {
  pointer-events: auto;
}

.elev-1 {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.bg-white {
  background-color: white !important;
}

/* Marker labels customization */
::v-deep .marker-label {
  margin-top: -50px;
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #FFCC80;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::v-deep .marker-label-rider {
  margin-top: -50px;
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #90CAF9;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::v-deep .marker-label-order {
  margin-top: -50px;
  background: white;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #A5D6A7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
